<template>
  <div class="body-parts-game">
    <LevelComplete v-if="gameOver" />
    <!-- Function container -->
    <div
      v-if="currentFunction"
      class="function-targets"
    >
      <div
        class="function-target"
        @dragover.prevent
        @drop="handleDrop($event, currentFunction.name)"
        @click="speakOutLoud(currentFunction.name)"
      >
        <h2 class="function-label">
          {{ currentFunction.name }}
        </h2>
        <img
          :src="currentFunction.image"
          alt="Function Image"
          class="function-image"
        >
        <!-- Display the correctly dropped items -->
        <div class="dropped-items">
          <span
            v-for="item in correctDrops[currentFunction.name]"
            :key="item.id"
          >{{ item.emoji }}</span>
        </div>
      </div>
    </div>

    <p
      v-if="message"
      class="message"
    >
      {{ message }}
    </p>

    <!-- Draggable body parts -->
    <div class="draggable-items">
      <div
        v-for="(item) in draggableItems"
        :key="item.id"
        class="draggable-emoji"
        :style="draggedItem === item ? draggedStyle : {}"
        draggable="true"
        @dragstart="handleDrag($event, item)"
        @click="speakOutLoud(item.name)"
        @touchstart="handleTouchStart($event, item)"
        @touchmove="handleTouchMove($event)"
        @touchend="handleTouchEnd($event)"
      >
        {{ item.emoji }}
      </div>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    const functionsList = [
      {
        name: "See",
        image:
          "https://img.freepik.com/free-vector/curiosity-search-concept-illustration_114360-30551.jpg?t=st=1736984135~exp=1736987735~hmac=6bd1477e099cd25b6a2dd53276f489334b26f9f5f9b7cbd16cc69b43d9b5861e&w=826",
      },
      {
        name: "Hear",
        image:
          "https://img.freepik.com/free-psd/hand-drawn-person-listening-music_23-2151546328.jpg?t=st=1736984188~exp=1736987788~hmac=f47ec7e628e3147199412ec55a799e4071c406980e073d7dfd2fc1b83a92e6eb&w=826",
      },
      {
        name: "Smell",
        image:
          "https://img.freepik.com/free-vector/hand-drawn-male-grooming-illustration_23-2150792373.jpg?t=st=1736984515~exp=1736988115~hmac=e24a8c71c45a2e2de7464f27a1e84592af2ef9c1d60203961f1ed81f57bb879a&w=1380",
      },
      {
        name: "Taste",
        image:
          "https://img.freepik.com/free-vector/hand-drawn-person-eating-illustration_23-2149191940.jpg?t=st=1736984608~exp=1736988208~hmac=6be7f288c371ea815102c7df1cec82c575148b152f08f90e22933fd182b2069b&w=826",
      },
      {
        name: "Touch",
        image:
          "https://img.freepik.com/free-vector/obsessive-compulsive-disorder-man_24908-82990.jpg?t=st=1736984442~exp=1736988042~hmac=b6a4547f0e652136195af69a98d676b7300e3b6d4b63ee4e95da72c1e99fdc0b&w=826",
      },
      {
        name: "Walk",
        image:
          "https://www.shutterstock.com/image-vector/cute-african-kid-go-school-600nw-2146471059.jpg",
      },
    ];
    const bodyPartEmojis = [
      { id: 1, emoji: "👁️", function: "See", name: "Eye" },
      { id: 2, emoji: "👂", function: "Hear", name: "Ear" },
      { id: 3, emoji: "👃", function: "Smell", name: "Nose" },
      { id: 4, emoji: "👄", function: "Taste", name: "Mouth" },
      { id: 5, emoji: "🤚", function: "Touch", name: "Hand" },
      { id: 6, emoji: "🦶", function: "Walk", name: "Foot" },
    ];
    const correctDrops = {};
    const totalItemsPerFunction = {};
    functionsList.forEach((func) => {
      correctDrops[func.name] = [];
      totalItemsPerFunction[func.name] = 0;
    });
    return {
      functionsList,
      bodyPartEmojis,
      draggableItems: [],
      totalItemsPerFunction,
      message: "",
      correctDrops,
      touchStartX: 0,
      touchStartY: 0,
      touchOffsetX: 0,
      touchOffsetY: 0,
      draggedItem: null,
      draggedStyle: {},
      selectedVoice: null,
      currentFunctionIndex: 0,
      gameOver: false,
    };
  },
  computed: {
    currentFunction() {
      if (this.gameOver) return null;
      return this.functionsList[this.currentFunctionIndex];
    },
  },
  mounted() {
    // Count total items per function
    this.bodyPartEmojis.forEach((item) => {
      this.totalItemsPerFunction[item.function]++;
    });

    // Shuffle items and assign positions
    this.draggableItems = this.bodyPartEmojis.slice(); // make a copy
    // Shuffle draggableItems
    this.draggableItems.sort(() => Math.random() - 0.5);

    // If voices aren't loaded immediately, wait for them
    if (typeof speechSynthesis === "undefined") {
      console.warn("Speech Synthesis API not supported in this browser.");
    } else {
      speechSynthesis.onvoiceschanged = () => this.setupVoices();
    }
    this.setupVoices();
  },
  methods: {
    handleDrag(event, item) {
      event.dataTransfer.setData("text/plain", JSON.stringify(item));
    },
    handleDrop(event, targetFunction) {
      const itemData = event.dataTransfer.getData("text/plain");
      const item = JSON.parse(itemData);
      if (item.function === targetFunction) {
        if (!this.correctDrops[targetFunction].some((i) => i.id === item.id)) {
          this.correctDrops[targetFunction].push(item);
          this.playSound(
            "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
          );
          // Remove the item from draggableItems
          this.draggableItems = this.draggableItems.filter(
            (i) => i.id !== item.id
          );
          // Speak the body part's name
          this.speakOutLoud(item.name);
          // Check if the function is complete
          if (
            this.correctDrops[targetFunction].length ===
            this.totalItemsPerFunction[targetFunction]
          ) {
            // Function completed
            this.currentFunctionIndex++;
            if (this.currentFunctionIndex >= this.functionsList.length) {
              // All functions completed
              this.speakOutLoud("Great Job!");
              this.gameOver = true;

              setTimeout(() => {
                this.$router.push("/body");
              }, 4000);
            }
          }
        } else {
          this.message = "Already placed!";
        }
      } else {
        this.message = "❌ Try again!";
        this.speakOutLoud("Try again!");
      }
      // Clear message after 2 seconds
      setTimeout(() => {
        this.message = "";
      }, 2000);
    },
    handleTouchStart(event, item) {
      const touchX = event.touches[0].clientX;
      const touchY = event.touches[0].clientY;
      this.touchStartX = touchX;
      this.touchStartY = touchY;
      this.draggedItem = item;

      // Get the bounding rect of the element
      const rect = event.currentTarget.getBoundingClientRect();
      this.touchOffsetX = touchX - rect.left;
      this.touchOffsetY = touchY - rect.top;

      this.draggedStyle = {
        position: "fixed",
        left: `${touchX - this.touchOffsetX}px`,
        top: `${touchY - this.touchOffsetY}px`,
        zIndex: 1000,
      };
    },
    handleTouchMove(event) {
      event.preventDefault(); // Prevent scrolling
      const touchX = event.touches[0].clientX;
      const touchY = event.touches[0].clientY;
      this.draggedStyle = {
        ...this.draggedStyle,
        left: `${touchX - this.touchOffsetX}px`,
        top: `${touchY - this.touchOffsetY}px`,
      };
    },
    handleTouchEnd(event) {
      const dropZone = this.$el.querySelector(".function-target");
      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;
      let dropped = false;

      if (dropZone) {
        const rect = dropZone.getBoundingClientRect();
        if (
          touchEndX >= rect.left &&
          touchEndX <= rect.right &&
          touchEndY >= rect.top &&
          touchEndY <= rect.bottom
        ) {
          const targetFunction = this.currentFunction.name;
          this.handleDrop(
            {
              dataTransfer: { getData: () => JSON.stringify(this.draggedItem) },
            },
            targetFunction
          );
          dropped = true;
        }
      }

      if (!dropped) {
        this.message = "❌ Try again!";
        this.speakOutLoud("Try again!");
        setTimeout(() => {
          this.message = "";
        }, 2000);
      }

      // Reset dragged item and style
      this.draggedItem = null;
      this.draggedStyle = {};
      this.touchOffsetX = 0;
      this.touchOffsetY = 0;
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
  },
};
</script>

<style scoped>
.body-parts-game {
  position: relative;
  width: 100%;
  height: 100vh;
}

/* Function targets */
.function-targets {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.function-target {
  width: 80%;
  max-width: 600px;
  background-color: #f0f0f0;
  border: 2px dashed #ccc;
  border-radius: 10px;
  position: relative;
  text-align: center;
  padding: 20px;
  cursor: pointer;
  margin: auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Function Label */
.function-label {
  font-size: 2rem;
  font-weight: bold;
  color: #333;
  text-transform: capitalize;
  margin-bottom: 15px;
  background-color: rgba(255, 255, 255, 0.8);
  display: inline-block;
  padding: 5px 15px;
  border-radius: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Function Image */
.function-image {
  width: 100%;
  max-height: 250px;
  object-fit: contain;
  border-radius: 10px;
  margin-bottom: 15px;
}

/* Dropped Items */
.dropped-items {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  min-height: 50px;
  padding: 5px;
  border-top: 1px solid #ddd;
}

.dropped-items span {
  font-size: 3rem;
  margin: 5px;
}

/* Messages */
.message {
  text-align: center;
  font-size: 2rem;
  font-weight: bold;
  color: #ff4d4f;
}

/* Draggable items */
.draggable-items {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: auto;
  width: 80%;
  padding: 20px 0;
}

.draggable-emoji {
  cursor: grab;
  font-size: 5rem;
  margin: 1rem;
  transition: transform 0.3s;
  user-select: none;
}

.draggable-emoji:active {
  cursor: grabbing;
}

.draggable-emoji:hover {
  transform: scale(1.2);
}

@media (hover: none) {
  .draggable-emoji:hover {
    transform: none;
  }
}
</style>